import type { KerningOverrides } from "./types";

// Illustrator-style compaction: drop overrides whose left or right character
// changed; remap overrides whose pair stayed intact in the suffix region.
export const compactKerningOverrides = (
  prevText: string,
  nextText: string,
  prevOverrides: KerningOverrides | undefined
): KerningOverrides | undefined => {
  if (!prevOverrides) return undefined;
  if (Object.keys(prevOverrides).length === 0) return undefined;
  if (prevText === nextText) {
    // Identity-preserving fast path: when the text is unchanged AND no orphan
    // overrides exist, return the input reference so downstream change-detection
    // (reference equality in Textbox.onChange) doesn't spuriously dispatch.
    let hasOrphan = false;
    for (const key of Object.keys(prevOverrides)) {
      if (Number(key) + 1 >= prevText.length) {
        hasOrphan = true;
        break;
      }
    }
    if (!hasOrphan) return prevOverrides;

    const copy: KerningOverrides = {};
    for (const [k, v] of Object.entries(prevOverrides)) {
      const idx = Number(k);
      if (idx + 1 < prevText.length) copy[idx] = v;
    }
    return Object.keys(copy).length > 0 ? copy : undefined;
  }

  const minLen = Math.min(prevText.length, nextText.length);
  let prefixLen = 0;
  while (prefixLen < minLen && prevText[prefixLen] === nextText[prefixLen]) {
    prefixLen++;
  }

  const maxSuffix = minLen - prefixLen;
  let suffixLen = 0;
  while (
    suffixLen < maxSuffix &&
    prevText[prevText.length - 1 - suffixLen] ===
      nextText[nextText.length - 1 - suffixLen]
  ) {
    suffixLen++;
  }

  const shift = nextText.length - prevText.length;
  const out: KerningOverrides = {};

  for (const [key, value] of Object.entries(prevOverrides)) {
    const idx = Number(key);
    if (!Number.isInteger(idx) || idx < 0) continue;

    // Pair (idx, idx+1) must both exist in next; orphans dropped.
    const pairFullyInPrefix = idx + 1 < prefixLen;
    const pairFullyInSuffix =
      idx >= prevText.length - suffixLen && idx + 1 <= prevText.length - 1;

    if (pairFullyInPrefix) {
      out[idx] = value;
    } else if (pairFullyInSuffix) {
      const remapped = idx + shift;
      if (remapped >= 0 && remapped + 1 <= nextText.length - 1) {
        out[remapped] = value;
      }
    }
  }

  return Object.keys(out).length > 0 ? out : undefined;
};
